import { toServerEmploymentType, normalizeGender } from '../services/profile_value_mapping';

type AnyObj = Record<string, any>;

const toNull = (v: any) => (v === '' ? null : v);

function toMonthStartISO(v: any) {
  if (!v) return null;
  if (/^\d{4}-\d{2}$/.test(v)) return `${v}-01`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v.slice(0, 10);
  return null;
}

const toNum = (v: any) => {
  if (v === '' || v === null || v === undefined) return null;
  const s = String(v).replace(',', '.').trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const toIsoDate = (v: any) => {
  if (!v) return null;
  const s = String(v).trim();
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
};

const PERSON_FIELDS = ['first_name', 'last_name', 'email', 'phone_number', 'birth_date', 'birth_place', 'nationality', 'gender', 'internal_personalnr', 'datev_personalnr'] as const;
const UNIFORM_FIELDS = ['clothing_size', 'clothing_cut', 'has_received_uniform'] as const;
const CONTACT_FIELDS = ['street', 'house_no', 'zip', 'city'] as const;
const JOB_FIELDS = [
  'employment_type',
  'work_areas',
  'primary_work_area',
  'position_title',
  'contract_type',
  'contract_end_date',
  'start_date',
  'probation_end_date',
] as const;
const WP_FIELDS = ['contract_hours_per_week', 'work_pattern_mode', 'regular_workdays', 'regular_workdays_per_week', 'hours_per_week', 'days_distribution'] as const;
const PAYROLL_FIELDS = [
  'steuer_id',
  'lohnsteuer_klasse',
  'sozialversicherungsnummer',
  'religion',
  'health_insurance_type',
  'health_insurance_name',
  'is_family_insured',
  'opening_balance_h',
  'opening_balance_effective_month',
  'opening_balance_note'
] as const;
const LEAVE_FIELDS = [
  'holiday_extra_days_per_year',
  'holiday_opening_balance_days',
  'holiday_balance_effective_year'
] as const;

function diffByKeys(initial: AnyObj, current: AnyObj, keys: readonly string[]) {
  const out: AnyObj = {};
  for (const k of keys) {
    const prev = initial?.[k];
    const next = current?.[k];

    // Merge-Guard: Don't overwrite defined values with undefined
    // Only allow null (explicit clearing) or defined values
    if (next === undefined && prev !== undefined) {
      continue;
    }

    const changed =
      Array.isArray(next) || typeof next === 'object'
        ? JSON.stringify(next ?? null) !== JSON.stringify(prev ?? null)
        : next !== prev;

    if (changed) out[k] = toNull(next);
  }
  return out;
}

export function prepareProfilePatch(initial: AnyObj, current: AnyObj) {
  const currPerson = { ...(current?.person || {}) };
  if ('birth_date' in currPerson) currPerson.birth_date = toIsoDate(currPerson.birth_date);
  const person = diffByKeys(initial?.person || {}, currPerson, PERSON_FIELDS);
  if ('gender' in person && person.gender !== undefined) {
    person.gender = normalizeGender(person.gender);
  }
  // Always include normalized gender when any person fields change,
  // to fix stale invalid gender values stored in the BE (e.g. from App input)
  if (Object.keys(person).length > 0 && !('gender' in person)) {
    person.gender = normalizeGender(currPerson.gender);
  }

  const contact = diffByKeys(initial?.contact || {}, current?.contact || {}, CONTACT_FIELDS);

  const currJob = { ...(current?.job || {}) };
  if ('start_date' in currJob) currJob.start_date = toIsoDate(currJob.start_date);
  if ('contract_end_date' in currJob) currJob.contract_end_date = toIsoDate(currJob.contract_end_date);
  if ('probation_end_date' in currJob) currJob.probation_end_date = toIsoDate(currJob.probation_end_date);
  if ('position_title' in currJob) currJob.position_title = currJob.position_title || null;
  if ('primary_work_area' in currJob) {
    currJob.primary_work_area = currJob.primary_work_area ? Number(currJob.primary_work_area) : null;
  }
  const job = diffByKeys(initial?.job || {}, currJob, JOB_FIELDS);

  if ('employment_type' in job && job.employment_type !== undefined) {
    job.employment_type = toServerEmploymentType(job.employment_type);
  }

  const initWP = { ...(initial?.working_pattern || {}) };
  const currWP = { ...(current?.working_pattern || {}) };
  if ('contract_hours_per_week' in currWP) {
    currWP.contract_hours_per_week = toNum(currWP.contract_hours_per_week);
  }
  if ('hours_per_week' in currWP) {
    currWP.hours_per_week = toNum(currWP.hours_per_week);
  }
  if ('days_distribution' in currWP) {
    const dist = Array.from({ length: 7 }, (_, i) => toNum(currWP?.days_distribution?.[i] ?? 0) ?? 0);
    currWP.days_distribution = dist;
  }
  const working_pattern = diffByKeys(initWP, currWP, WP_FIELDS);

  const currPayroll = { ...(current?.payroll || {}) };
  if ('opening_balance_effective_month' in currPayroll) {
    currPayroll.opening_balance_effective_month = toMonthStartISO(currPayroll.opening_balance_effective_month);
  }
  if ('opening_balance_h' in currPayroll) {
    currPayroll.opening_balance_h = toNum(currPayroll.opening_balance_h);
  }
  const payroll = diffByKeys(initial?.payroll || {}, currPayroll, PAYROLL_FIELDS);

  const currLeave = { ...(current?.leave || {}) };
  if ('holiday_extra_days_per_year' in currLeave) {
    currLeave.holiday_extra_days_per_year = toNum(currLeave.holiday_extra_days_per_year);
  }
  if ('holiday_opening_balance_days' in currLeave) {
    currLeave.holiday_opening_balance_days = toNum(currLeave.holiday_opening_balance_days);
  }
  const leave = diffByKeys(initial?.leave || {}, currLeave, LEAVE_FIELDS);

  const uniform = diffByKeys(initial || {}, current || {}, UNIFORM_FIELDS);

  const initialQualIds = ((initial?.qualifications ?? []) as any[]).map((q: any) => q.id).sort();
  const currentQualIds = ((current?.qualifications ?? []) as any[]).map((q: any) => q.id).sort();
  const qualificationsChanged = JSON.stringify(initialQualIds) !== JSON.stringify(currentQualIds);

  const patch: AnyObj = {};
  if (Object.keys(person).length) patch.person = person;
  if (Object.keys(contact).length) patch.contact = contact;
  if (Object.keys(job).length) patch.job = job;
  if (Object.keys(working_pattern).length) patch.working_pattern = working_pattern;
  if (Object.keys(payroll).length) patch.payroll = payroll;
  if (Object.keys(leave).length) patch.leave = leave;
  if (qualificationsChanged) patch.qualifications = currentQualIds;
  // Uniform fields are root-level on Employee model
  Object.assign(patch, uniform);

  return patch;
}
